import { inject, Injectable } from '@angular/core';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { UserConfigsWMSListModel } from '@kanzi-apes/kanzi-models';
import { EnvironmentService } from '@kanzi-apes/kanzi-utils';
import {
  ForgotPasswordFormModel,
  LoginChangePasswordRequired,
  LoginCognitoSuccess,
  LoginFormModel,
  NewPasswordFormModel,
  UserConfigsWMSFilterModel,
} from '../../models/interfaces';
import { Observable } from 'rxjs';
import { KanziRestClientService } from '@kanzi-apes/kanzi-rest-api';
import { SessionExpiredService } from '../session-expired/session-expired.service';

/**
 * @author Hugo Andrés Escobar Ciceri
 * @version 7.0.0
 *
 * Service with the login and authentication functions.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpClient = inject(KanziRestClientService);
  private envService = inject(EnvironmentService);
  private sessionExpiredService = inject(SessionExpiredService);
  private userPool: CognitoUserPool | null = null;
  private userConfigsWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.userConfigs.apiURL}`;
  private userConfigsWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.userConfigs.apiPath}`;
  private tokenRefreshTimer: any = null;
  cognitoUser?: CognitoUser;
  poolData = {
    UserPoolId:
      this.envService.wmsEnvironment?.cognitoConfig?.cognitoUserPoolId || '',
    ClientId:
      this.envService.wmsEnvironment?.cognitoConfig?.cognitoAppClientId || '',
  };

  get cognitoUserObject(): CognitoUser | undefined {
    return this.cognitoUser;
  }

  /**
   * Method to get the current authenticated user.
   * @returns CognitoUser | null
   */
  currentAuthenticatedUser(): CognitoUser | null {
    this.userPool = new CognitoUserPool(this.poolData);
    return this.userPool.getCurrentUser();
  }

  /**
   * Method to get the current authenticated user.
   * @returns CognitoUser | null
   */
  currentAuthenticatedUserObservable(): Observable<CognitoUser | null> {
    this.userPool = new CognitoUserPool(this.poolData);
    return new Observable((observer) => {
      if (this.userPool) {
        let user = this.userPool.getCurrentUser();
        if (user) {
          this.cognitoUser = user;
          observer.next(user);
          observer.complete();
        } else {
          observer.next(null);
          observer.complete();
        }
      } else {
        observer.error('UserPool not found');
      }
    });
  }

  getSessionValidUserObservable(
    userCognito: CognitoUser | null
  ): Observable<boolean> {
    return new Observable((observer) => {
      if (!userCognito) {
        observer.next(false);
        observer.complete();
      }
      userCognito?.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          observer.error(err);
        } else {
          observer.next(session.isValid());
          observer.complete();
        }
      });
    });
  }

  getUserAttributesObservable(userCognito: CognitoUser): Observable<any> {
    return new Observable((observer) => {
      userCognito.getUserAttributes((err, result) => {
        if (err) {
          observer.error(err);
        } else {
          observer.next(
            result?.map((attr) => {
              return { [attr.getName()]: attr.getValue() };
            })
          );
          observer.complete();
        }
      });
    });
  }

  /**
   * Method to refresh the user session using the refresh token
   * @returns Observable<CognitoUserSession>
   */
  refreshSession(): Observable<CognitoUserSession> {
    return new Observable((observer) => {
      const currentUser = this.currentAuthenticatedUser();

      if (!currentUser) {
        observer.error('No user is currently authenticated');
        return;
      }

      this.cognitoUser = currentUser;

      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          observer.error(err);
          return;
        }

        const refreshToken = session.getRefreshToken();

        currentUser.refreshSession(refreshToken, (refreshErr, newSession) => {
          if (refreshErr) {
            observer.error(refreshErr);
          } else {
            this.persistIdToken(newSession);
            observer.next(newSession);
            observer.complete();
          }
        });
      });
    });
  }

  /**
   * Method to check if the token needs refresh and refresh it automatically
   * @returns Observable<CognitoUserSession | null>
   */
  checkAndRefreshToken(): Observable<CognitoUserSession | null> {
    return new Observable((observer) => {
      const currentUser = this.currentAuthenticatedUser();

      if (!currentUser) {
        observer.next(null);
        observer.complete();
        return;
      }

      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          observer.error(err);
          return;
        }

        // Check if token is still valid
        if (session.isValid()) {
          this.persistIdToken(session);
          observer.next(session);
          observer.complete();
        } else {
          // Token expired, refresh it
          const refreshToken = session.getRefreshToken();

          currentUser.refreshSession(refreshToken, (refreshErr, newSession) => {
            if (refreshErr) {
              observer.error(refreshErr);
            } else {
              this.persistIdToken(newSession);
              observer.next(newSession);
              observer.complete();
            }
          });
        }
      });
    });
  }

  /**
   * Persists the current IdToken to localStorage under `KanziWMSUserToken`.
   * Keeps the stored token in sync whenever the session is validated or
   * refreshed, so API calls that read it (Cognito-authorized endpoints)
   * always send a fresh, non-expired token instead of the one captured only
   * at login time.
   * @param session - The valid or refreshed Cognito user session
   */
  private persistIdToken(session: CognitoUserSession): void {
    if (session && session.isValid()) {
      localStorage.setItem(
        'KanziWMSUserToken',
        session.getIdToken().getJwtToken()
      );
    }
  }

  /**
   * Method to start automatic token refresh based on expiration time
   * Refreshes token 5 minutes before expiration
   */
  startTokenRefreshTimer(): void {
    const currentUser = this.currentAuthenticatedUser();

    if (!currentUser) {
      return;
    }

    currentUser.getSession((err: any, session: CognitoUserSession) => {
      if (err || !session.isValid()) {
        return;
      }

      // Get token expiration time
      const expiresAt = session.getAccessToken().getExpiration() * 1000; // Convert to milliseconds
      const currentTime = new Date().getTime();
      const timeUntilExpiry = expiresAt - currentTime;

      // Refresh 5 minutes (300000ms) before expiration
      const refreshTime = Math.max(0, timeUntilExpiry - 300000);

      // Clear existing timer
      if (this.tokenRefreshTimer) {
        clearTimeout(this.tokenRefreshTimer);
      }

      // Set new timer
      this.tokenRefreshTimer = setTimeout(() => {
        this.refreshSession().subscribe({
          next: (newSession) => {
            console.log('Token refreshed successfully');
            // Restart the timer for the next refresh
            this.startTokenRefreshTimer();
          },
          error: (error) => {
            console.error('Error refreshing token:', error);
            this.sessionExpiredService.showSessionExpiredDialog();
          },
        });
      }, refreshTime);
    });
  }

  /**
   * Method to stop the automatic token refresh timer
   */
  stopTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  /**
   * Method to get the current session and refresh if needed
   * @returns Observable<CognitoUserSession>
   */
  getCurrentSession(): Observable<CognitoUserSession> {
    return new Observable((observer) => {
      const currentUser = this.currentAuthenticatedUser();

      if (!currentUser) {
        observer.error('No user is currently authenticated');
        return;
      }

      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          observer.error(err);
          return;
        }

        // If session is valid, return it
        if (session.isValid()) {
          observer.next(session);
          observer.complete();
        } else {
          // Session expired, try to refresh
          const refreshToken = session.getRefreshToken();

          currentUser.refreshSession(refreshToken, (refreshErr, newSession) => {
            if (refreshErr) {
              observer.error(refreshErr);
            } else {
              observer.next(newSession);
              observer.complete();
            }
          });
        }
      });
    });
  }

  /**
   * Method to loging the user.
   * @param loginForm {LoginFormModel} - Login form model
   * @returns Observable<CognitoUserSession>
   */
  loginUser(
    loginForm: LoginFormModel
  ): Observable<LoginCognitoSuccess | LoginChangePasswordRequired> {
    let authenticationDetails = new AuthenticationDetails({
      Username: loginForm.username,
      Password: loginForm.password,
    });
    this.userPool = new CognitoUserPool(this.poolData);
    let userData = { Username: loginForm.username, Pool: this.userPool };
    this.cognitoUser = new CognitoUser(userData);
    return new Observable((observer) => {
      this.cognitoUser?.authenticateUser(authenticationDetails, {
        onSuccess: (userSession, userConfirmation) => {
          observer.next({
            userSession: userSession,
            userConfirmation: userConfirmation,
          });
          observer.complete();
        },
        newPasswordRequired: (userAttibutes, requiredAttributes) => {
          observer.next({
            challengeName: 'NEW_PASSWORD_REQUIRED',
            userAttibutes,
            requiredAttributes,
          });
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(err);
        },
      });
    });
  }

  /**
   * Method to logout the user.
   * @returns Observable<boolean> - Observable with the logout result.
   */
  logoutUser(): Observable<boolean> {
    this.userPool = new CognitoUserPool(this.poolData);
    let cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
      return new Observable((observer) => {
        observer.next(true);
        observer.complete();
      });
    } else {
      return new Observable((observer) => {
        observer.error('No user found');
      });
    }
  }

  /**
   * Method to confirm the new password.
   * @param newPassword {NewPasswordFormModel} - New password form model
   * @returns Oberable<CognitoUserSession>
   */
  changeNewPassword(
    newPassword: NewPasswordFormModel
  ): Observable<CognitoUserSession> {
    return new Observable((observer) => {
      if (this.cognitoUser) {
        this.cognitoUser.completeNewPasswordChallenge(
          newPassword.newPassword,
          {},
          {
            onSuccess: (userSession) => {
              observer.next(userSession);
              observer.complete();
            },
            onFailure: (err) => {
              observer.error(err);
              observer.complete();
            },
          }
        );
      }
    });
  }

  /**
   * Method to restore the password.
   * @param username {string} - Username to restore the password
   * @returns Observable<any>
   */
  restorePassword(username: string): Observable<any> {
    return new Observable((observer) => {
      this.userPool = new CognitoUserPool(this.poolData);
      let userData = { Username: username, Pool: this.userPool };
      this.cognitoUser = new CognitoUser(userData);
      this.cognitoUser.forgotPassword({
        onSuccess: (data) => {
          observer.next(data);
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(err);
          observer.complete();
        },
      });
    });
  }

  /**
   * Metheod to confirm the new password.
   * @param confirmPassword {ForgotPasswordFormModel} - Confirm Password Model
   * @returns Observable<any> - Observable with the result of the confirmation.
   */
  confirmNewPassword(
    confirmPassword: ForgotPasswordFormModel
  ): Observable<any> {
    return new Observable((observer) => {
      if (this.cognitoUser) {
        this.cognitoUser.confirmPassword(
          confirmPassword.code,
          confirmPassword.new_password,
          {
            onSuccess: (data) => {
              observer.next(data);
              observer.complete();
            },
            onFailure: (err) => {
              observer.error(err);
            },
          }
        );
      }
    });
  }

  obtainConfigUser(
    confiFilters: UserConfigsWMSFilterModel
  ): Observable<UserConfigsWMSListModel> {
    const url = `${this.userConfigsWMSUrl}${this.userConfigsWMSPath}`;
    return this.httpClient.get(
      url,
      confiFilters,
      'Error al listar las configuraciones',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  /**
   * Method to get current user data after validating session
   * @returns Observable<any> - Observable with user data
   */
  getCurrentUserData(): Observable<any> {
    return new Observable((observer) => {
      const currentUser = this.currentAuthenticatedUser();

      if (!currentUser) {
        observer.error('No user is currently authenticated');
        return;
      }

      // Primero verificar que la sesión es válida
      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err || !session || !session.isValid()) {
          observer.error('Invalid session');
          return;
        }

        // Ahora obtener los datos del usuario
        currentUser.getUserData((userErr, userData) => {
          if (userErr) {
            observer.error(userErr);
          } else {
            observer.next(userData);
            observer.complete();
          }
        });
      });
    });
  }

  /**
   * Method to get user attributes in a reactive way
   * @returns Observable<any[]>
   */
  getCurrentUserAttributes(): Observable<any[]> {
    return new Observable((observer) => {
      const currentUser = this.currentAuthenticatedUser();

      if (!currentUser) {
        observer.error('No user is currently authenticated');
        return;
      }

      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err || !session || !session.isValid()) {
          observer.error('Invalid session');
          return;
        }

        currentUser.getUserAttributes((attrErr, attributes) => {
          if (attrErr) {
            observer.error(attrErr);
          } else {
            observer.next(attributes || []);
            observer.complete();
          }
        });
      });
    });
  }
}
